// BoundaryManager.js

import Boundary from './Boundary';
import jsts from 'jsts';

class BoundaryManager {
  static createNewBoundary() {
    // console.log("Creating new boundary");
    return new Boundary([]);
  }

  // Move only the boundary with id === boundaryId. Multiplier (default 1)
  // scales the step — used by the keydown handler to pass `shiftKey ? 10 : 1`
  // so holding Shift moves the selection in big jumps.
  static handleEditBoundary(key, boundaryId, boundaries, setBoundaries, multiplier = 1) {
    const step = 10 * multiplier;
    const direction = {
      ArrowUp: { dx: 0, dy: -step },
      ArrowDown: { dx: 0, dy: step },
      ArrowLeft: { dx: -step, dy: 0 },
      ArrowRight: { dx: step, dy: 0 },
    };
    if (!direction[key] || !boundaryId) return;
    const updatedBoundaries = boundaries.map(boundary => {
      if (boundary.id !== boundaryId) return boundary;
      return new Boundary(
        boundary.points.map(point => [
          point[0] + direction[key].dx,
          point[1] + direction[key].dy,
        ]),
        boundary.id,
        boundary.isInnerBoundary
      );
    });
    setBoundaries(updatedBoundaries);
  }

  // static updateInnerBoundaries(boundaries, distance) {
  //   return boundaries.map(boundary => 
  //     this.createInnerBoundary(boundary.points, distance)
  //   );
  // }

  static createInnerBoundary(outerPoints, distanceInPixels) {
    if (outerPoints.length < 3) return null;

    const geometryFactory = new jsts.geom.GeometryFactory();

    // Create a JSTS LineString from the outer points
    const coordinates = outerPoints.map(point => new jsts.geom.Coordinate(point[0], point[1]));
    coordinates.push(coordinates[0]); // Close the ring
    const shell = geometryFactory.createLinearRing(coordinates);
    const polygon = geometryFactory.createPolygon(shell);

    // Create the inner boundary
    const bufferedGeom = polygon.buffer(-distanceInPixels);

    // Extract the coordinates of the inner boundary
    let innerBoundary;
    if (bufferedGeom.isEmpty()) {
      console.warn("Inner boundary is empty. The buffer distance might be too large.");
      return null;
    } else if (bufferedGeom instanceof jsts.geom.Polygon) {
      innerBoundary = bufferedGeom.getExteriorRing().getCoordinates();
    } else if (bufferedGeom instanceof jsts.geom.MultiPolygon) {
      // If the result is a MultiPolygon, we'll just take the first polygon
      innerBoundary = bufferedGeom.getGeometryN(0).getExteriorRing().getCoordinates();
    } else {
      console.error("Unexpected geometry type after buffering");
      return null;
    }

    // Convert back to simple array format and remove the last point (which is a duplicate of the first)
    return innerBoundary.slice(0, -1).map(coord => [Math.round(coord.x), Math.round(coord.y)]);
  }

  static handleEditBoundaryPoint(key, pointIndex, boundaryId, boundaries, setBoundaries, setSelectedBoundaryPoint, multiplier = 1) {
    const step = 10 * multiplier;
    const direction = {
      ArrowUp: { dx: 0, dy: -step },
      ArrowDown: { dx: 0, dy: step },
      ArrowLeft: { dx: -step, dy: 0 },
      ArrowRight: { dx: step, dy: 0 },
    };
    if (!direction[key]) return;
    setBoundaries(prevBoundaries => prevBoundaries.map(boundary => {
      // Only update the owning boundary. Without this guard a point at the
      // same index in OTHER boundaries would also move.
      if (boundaryId && boundary.id !== boundaryId) return boundary;
      if (boundary.points[pointIndex]) {
        boundary.updatePoint(pointIndex, direction[key].dx, direction[key].dy);
      }
      return boundary;
    }));
    setSelectedBoundaryPoint(prevPoint => {
      if (!prevPoint) return prevPoint;
      return [
        prevPoint[0] + direction[key].dx,
        prevPoint[1] + direction[key].dy,
      ];
    });
  }

  static isPointNearBoundary(point, boundary, threshold) {
    return boundary.isPointNear(point, threshold);
  }

  static findClosestBoundary(point, boundaries, threshold) {
    let closestBoundary = null;
    let minDistance = Infinity;

    boundaries.forEach(boundary => {
      const distance = boundary.distanceToPoint(point);
      if (distance < minDistance && distance <= threshold) {
        minDistance = distance;
        closestBoundary = boundary;
      }
    });

    return closestBoundary;
  }

  static findClosestBoundaryPoint(point, boundaries, threshold) {
    let closestPoint = null;
    let closestBoundary = null;
    let closestPointIndex = -1;
    let minDistance = Infinity;

    boundaries.forEach(boundary => {
      boundary.points.forEach((boundaryPoint, index) => {
        const distance = Math.hypot(point[0] - boundaryPoint[0], point[1] - boundaryPoint[1]);
        if (distance < minDistance && distance <= threshold) {
          minDistance = distance;
          closestPoint = boundaryPoint;
          closestBoundary = boundary;
          closestPointIndex = index;
        }
      });
    });

    return { point: closestPoint, boundary: closestBoundary, index: closestPointIndex };
  }
}

export default BoundaryManager;